import React, { useEffect, useState } from 'react';
import { Button, Drawer, Popconfirm, Skeleton, Tag, Typography, message } from 'antd';
import Markdown from '../Markdown';
import LinkedTasks from './LinkedTasks';
import LinkedWikis from './LinkedWikis';
import api from '../../lib/api';
import type { WorklogFull, WorklogSummary, WorklogTaskType } from '../../types/worklog';
import { WORKLOG_TASK_TYPE_LABELS } from '../../types/worklog';

type Props = {
  worklog: WorklogSummary | null;
  onClose: () => void;
  onDeleted: () => void;
};

const WorklogDrawer: React.FC<Props> = ({ worklog, onClose, onDeleted }) => {
  const [full, setFull] = useState<WorklogFull | null>(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // 正文不进列表 props，抽屉打开时才取。切换条目时先清掉旧正文，
  // 否则加载新条目的瞬间会看到上一条的内容。
  useEffect(() => {
    if (!worklog) return;

    let cancelled = false;
    setFull(null);
    setLoading(true);

    api.worklogs
      .get(worklog.id)
      .then((data: WorklogFull) => {
        if (!cancelled) setFull(data);
      })
      .catch(() => {
        if (!cancelled) message.error('正文加载失败');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [worklog?.id]);

  const handleDelete = async () => {
    if (!worklog) return;

    setDeleting(true);
    try {
      await api.worklogs.destroy(worklog.id);
      message.success('已删除');
      onDeleted();
    } catch {
      message.error('删除失败');
    } finally {
      setDeleting(false);
    }
  };

  const taskType = worklog?.task_type as WorklogTaskType | undefined;

  return (
    <Drawer
      open={!!worklog}
      onClose={onClose}
      width={720}
      title={worklog?.title}
      extra={
        <div className="flex items-center gap-3">
          {/* /worklog/:slug 这个页面一直存在，但从日志这个子系统里走不到它 ——
              能跳进去的只有任务右栏的「相关日志」，是从外面进来的。抽屉是组件 state 不进 URL（Worklog.tsx 立过「URL 决定
              内容」这条规矩），所以「发给别人」「在新标签页留着」在抽屉里做不到，
              这个链接就是那扇门。
              放标题栏而不是把下面那行 slug 变成链接：slug 是灰色小字标识符，
              人不会把它读成可点的东西；extra 才是 antd Drawer 放动作的位置。 */}
          {worklog && (
            <a
              href={`/worklog/${worklog.slug}`}
              className="text-xs text-blue-600 hover:underline"
            >
              在单独页面打开 ↗
            </a>
          )}
          <Popconfirm
            title="删除这条日志？"
            description="删了就没了，不能恢复。"
            okText="删除"
            cancelText="取消"
            okButtonProps={{ danger: true, loading: deleting }}
            onConfirm={handleDelete}
          >
            <Button danger size="small">
              删除
            </Button>
          </Popconfirm>
        </div>
      }
    >
      {worklog && (
        <div className="mb-4 flex flex-col gap-1">
          <div>
            <Tag>{taskType ? WORKLOG_TASK_TYPE_LABELS[taskType] ?? taskType : ''}</Tag>
            <Typography.Text type="secondary">
              {new Date(worklog.completed_at).toLocaleString('zh-CN', { hour12: false })}
            </Typography.Text>
          </div>
          <Typography.Text type="secondary" className="text-xs break-all">
            {worklog.working_directory ?? '未记录工作目录'}
          </Typography.Text>
          <Typography.Text type="secondary" className="text-xs break-all">
            {worklog.slug}
          </Typography.Text>
        </div>
      )}

      {loading && <Skeleton active paragraph={{ rows: 8 }} />}

      {/* 与单条日志页共用同一个组件 —— 两处是同一份内容的两种读法。
          没有关联时组件自己返回 null。 */}
      {!loading && full && <LinkedTasks tasks={full.linked_tasks} />}
      {!loading && full && <LinkedWikis wikis={full.linked_wikis} />}

      {!loading && full && !full.body_md && (
        <Typography.Text type="secondary">这条日志没有正文。</Typography.Text>
      )}

      {!loading && full?.body_md && <Markdown>{full.body_md}</Markdown>}
    </Drawer>
  );
};

export default WorklogDrawer;
